/**
 * Віджет підтримки: кругла кнопка «?» праворуч унизу (ч.1011).
 *
 * Зауваження власника до ч.1010: я вбудував форму у сторінку замість
 * віджета. Правильно — як на прикладі власника: кнопка висить поверх
 * будь-якої сторінки, розкривається панеллю збоку, людина пише питання
 * не залишаючи роботи.
 *
 * Панель свідомо тонка: список звернень і повна переписка — на сторінці
 * `/support` (кнопка «відкрити у вікні»). Тут — швидко поставити питання
 * і побачити відповідь.
 *
 * ч.1116 (рішення власника): кнопка «як це працює» — посилання на статті
 * довідки про ПОТОЧНУ сторінку (до трьох і «ще N»). Після переходу на іншу
 * сторінку показані посилання зникають: вони були про попередню.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { help } from '@/api/help';
import { support, type CaseStatus, type SupportCase } from '@/api/support';
import { useAuth } from '@/auth/store';

/** Скільки статей «як це працює» видно одразу; решта — за посиланням «ще». */
const HOW_VISIBLE = 3;

/** Вирішені й закриті звернення у віджеті не показуємо (ч.1086, рішення
 *  власника) — їх історія лишається на сторінці /support. */
const isFinished = (s: CaseStatus) => s === 'RESOLVED' || s === 'CLOSED';

/** ч.1242: стеля розміру знімка (символів data-URL) — трохи нижче ліміту сервера. */
const MAX_SHOT_CHARS = 2_700_000;
/** Ширина, до якої знімаємо у повному розмірі: більше модель усе одно зменшить. */
const SHOT_MAX_WIDTH = 1568;

/** Масштаби знімка екрана: спершу повний розмір (або до 1568 px завширшки), далі менші —
 *  на випадок, коли сторінка не влазить у ліміт. */
export function shotScales(viewportWidth: number): number[] {
  const full = Math.min(1, SHOT_MAX_WIDTH / Math.max(1, viewportWidth));
  return [full, full * 0.75, full * 0.5].map((s) => Math.round(s * 100) / 100);
}

/** ч.1241: як часто питаємо сервер про непрочитані відповіді, поки вікно згорнуте. */
const UNREAD_POLL_MS = 20_000;

/**
 * Яке звернення показати при відкритті вікна. ч.1241: спершу те, де є непрочитана відповідь,
 * — навіть якщо його вже закрили (відповідь спеціаліста часто й закриває звернення, а людина
 * її ще не бачила); далі — останнє незакрите (ч.1086); немає — чисте «Поставте запитання».
 */
export function pickCaseToOpen(cases: { id: string; status: CaseStatus; unread?: number }[]): string | null {
  return (cases.find((c) => (c.unread ?? 0) > 0) ?? cases.find((c) => !isFinished(c.status)))?.id ?? null;
}

export default function SupportWidget() {
  const token = useAuth((s) => s.sessionActive);
  const tenantId = useAuth((s) => s.activeTenantId);
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [withShot, setWithShot] = useState(false);
  const feedRef = useRef<HTMLDivElement>(null);

  // ── «як це працює» (ч.1116) ──
  // Памʼятаємо, ДЛЯ ЯКОЇ сторінки відкрито перелік: порівняння з поточною
  // адресою ховає його одразу після переходу (без запиту про нову сторінку),
  // а ефект скидає стан — повернення на ту саму сторінку перелік не «оживляє».
  const pageKey = location.pathname + location.search;
  const [howFor, setHowFor] = useState<string | null>(null);
  const [howAll, setHowAll] = useState(false);
  const howOpen = howFor === pageKey;
  useEffect(() => { setHowFor(null); setHowAll(false); }, [pageKey]);
  const how = useQuery({
    queryKey: ['help-page-articles', pageKey],
    queryFn: () => help.pageArticles(pageKey),
    enabled: open && howOpen,
    staleTime: 5 * 60_000,
  });
  const howItems = how.data?.items ?? [];

  const list = useQuery({
    queryKey: ['support-cases'],
    queryFn: support.cases,
    enabled: open && !!token && !!tenantId,
  });

  const active = useQuery({
    queryKey: ['support-case', activeId],
    queryFn: () => support.case(activeId!),
    enabled: !!activeId && open,
    refetchInterval: 15_000,
  });

  // ч.1241 (запит власника): вікно згорнуте, а підтримка відповіла — на кнопці позначка з
  // числом непрочитаних. Опитуємо лише поки вікно закрите (відкрите — людина і так бачить
  // переписку, а її перегляд знімає позначку на сервері).
  const onSupportPage = location.pathname.startsWith('/support');
  const unreadQ = useQuery({
    queryKey: ['support-unread'],
    queryFn: support.unread,
    enabled: !!token && !!tenantId && !open && !onSupportPage,
    refetchInterval: UNREAD_POLL_MS,
    staleTime: 0,
  });
  const unread = open ? 0 : (unreadQ.data?.count ?? 0);
  // Переписку показали — позначку скидаємо одразу, не чекаючи наступного опитування.
  useEffect(() => {
    if (active.data) qc.setQueryData(['support-unread'], { count: 0, cases: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active.dataUpdatedAt]);

  // Відкриваємо останнє НЕЗАКРИТЕ звернення: людина частіше повертається за
  // відповіддю, ніж починає нове. ч.1086 (рішення власника): вирішені й
  // закриті діалоги не показуємо — немає відкритого, то чисте «Поставте
  // запитання». Вибір робимо при КОЖНОМУ відкритті панелі і лише за свіжим
  // списком із сервера (не за кешем): звернення могло закритись, поки
  // панель була згорнута. Поки панель відкрита, поточний діалог не ховаємо,
  // навіть якщо його щойно закрили, — людина дочитує відповідь.
  const openedAt = useRef<number | null>(null);
  useEffect(() => {
    if (open) {
      openedAt.current = Date.now();
      // Свіжий список навіть якщо кеш ще «свіжий»; не скасовуючи запит,
      // що вже пішов при ввімкненні query.
      void list.refetch({ cancelRefetch: false });
    } else {
      openedAt.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    if (openedAt.current === null || !list.data) return;
    if (list.dataUpdatedAt < openedAt.current) return;   // ще кеш до відкриття
    openedAt.current = null;                              // вибрали — до наступного відкриття
    setActiveId(pickCaseToOpen(list.data));
  }, [list.data, list.dataUpdatedAt]);

  useEffect(() => {
    feedRef.current?.scrollTo({ top: feedRef.current.scrollHeight });
  }, [active.data?.messages.length]);

  /** Знімок поточної сторінки (ч.1012). Панель віджета ховаємо, щоб вона
   *  не перекривала те, на що людина скаржиться.
   *  ч.1242: знімок читає і агент підтримки, тому знімаємо у повному розмірі (ширина до
   *  1568 px — більше модель усе одно зменшить); на половинному масштабі текст не читався.
   *  Не влазить у ліміт сервера — пробуємо менший масштаб. */
  const grab = async (): Promise<string | null> => {
    if (!withShot) return null;
    const el = document.querySelector('.supw') as HTMLElement | null;
    try {
      if (el) el.style.visibility = 'hidden';
      const { default: html2canvas } = await import('html2canvas');
      const width = document.documentElement.clientWidth;
      for (const scale of shotScales(width)) {
        const canvas = await html2canvas(document.body, {
          logging: false, useCORS: true, scale,
          windowWidth: width,
          windowHeight: document.documentElement.clientHeight,
        });
        const data = canvas.toDataURL('image/png');
        if (data.length <= MAX_SHOT_CHARS) return data;
      }
      return null;   // навіть найменший не вліз — лист усе одно має піти
    } catch {
      return null;   // не змогли зняти — лист усе одно має піти
    } finally {
      if (el) el.style.visibility = '';
    }
  };

  const send = useMutation({
    mutationFn: async (): Promise<SupportCase> => {
      const body = draft.trim();
      const shot = await grab();
      if (activeId) return support.reply(activeId, body, shot);
      // Нове звернення з віджета: заголовок робимо з першого рядка —
      // окреме поле «заголовок» у швидкому вікні лише заважає.
      const title = body.length > 60
        ? `${body.slice(0, 57).trimEnd()}…` : body;
      return support.create({
        title, body,
        // ч.1035: головна («/») — власний шаблон `home`: щоб у неї була своя
        // стаття довідки (віджети) і агент її знаходив; раніше — null.
        route_pattern: location.pathname.replace(/^\//, '') || 'home',
        screenshot: shot,
      });
    },
    onSuccess: (c) => {
      setDraft('');
      setWithShot(false);   // ч.1178: знімок — разова дія, галочка скидається після відправки
      setActiveId(c.id);
      qc.invalidateQueries({ queryKey: ['support-cases'] });
      qc.invalidateQueries({ queryKey: ['support-case', c.id] });
    },
  });

  // Гостю і до вибору тенанта віджет не показуємо: звернення прив'язане
  // до облікового запису.
  if (!token || !tenantId) return null;
  // На самій сторінці підтримки віджет зайвий.
  if (onSupportPage) return null;

  const doc = active.data;

  return (
    <>
      {open && (
        <aside className="supw" role="dialog" aria-label="Служба підтримки">
          <header className="supw__head">
            <span className="supw__title">Служба підтримки</span>
            <span className="supw__head-btns">
              <button type="button" title="Нове звернення" aria-label="Нове звернення"
                onClick={() => { setActiveId(null); setDraft(''); }}>+</button>
              <button type="button" title="Відкрити у вікні" aria-label="Відкрити у вікні"
                onClick={() => { setOpen(false); navigate('/support'); }}>⧉</button>
              <button type="button" title="Закрити" aria-label="Закрити"
                onClick={() => setOpen(false)}>✕</button>
            </span>
          </header>

          <div className="supw__how">
            <button type="button" className="supw__how-btn" aria-expanded={howOpen}
              title="Статті довідки про сторінку, на якій ви зараз"
              onClick={() => { setHowAll(false); setHowFor(howOpen ? null : pageKey); }}>
              як це працює
            </button>
            {howOpen && (
              <div className="supw__how-list" data-testid="supw-how">
                {how.isLoading && <div className="supw__how-note">шукаємо статті…</div>}
                {how.isError && (
                  <div className="supw__how-note">Не вдалося завантажити довідку — спробуйте ще раз.</div>
                )}
                {how.data && howItems.length === 0 && (
                  <div className="supw__how-note">
                    Про цю сторінку статті ще немає — поставте запитання нижче або{' '}
                    <button type="button" className="supw__how-more"
                      onClick={() => { setOpen(false); navigate('/help'); }}>
                      відкрийте довідку
                    </button>.
                  </div>
                )}
                {how.data?.video_url && (
                  <a className="supw__hint" data-testid="supw-video" href={how.data.video_url}
                    target="_blank" rel="noreferrer" title="Відео-інструкція до цієї сторінки на YouTube">
                    відео-інструкція до сторінки
                  </a>
                )}
                {(how.data?.videos ?? []).map((v) => (
                  <a key={v.url} className="supw__hint" data-testid="supw-related-video" href={v.url}
                    target="_blank" rel="noreferrer"
                    title={v.kind === 'FAQ' ? 'Відео-відповідь на часте питання (YouTube)' : 'Відео-інструкція (YouTube)'}>
                    відео: {v.title}
                  </a>
                ))}
                {(howAll ? howItems : howItems.slice(0, HOW_VISIBLE)).map((a) => (
                  <button key={a.slug} type="button" className="supw__hint"
                    onClick={() => { setOpen(false); navigate(`/help/${a.slug}`); }}>
                    {a.title}
                  </button>
                ))}
                {howItems.length > HOW_VISIBLE && (
                  <button type="button" className="supw__how-more"
                    onClick={() => setHowAll((v) => !v)}>
                    {howAll ? 'згорнути' : `ще ${howItems.length - HOW_VISIBLE}`}
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="supw__feed" ref={feedRef}>
            {!doc && (
              <div className="supw__empty">Поставте запитання</div>
            )}
            {doc && doc.suggested.length > 0 && (
              <div className="supw__hints">
                <div className="supw__hints-title">
                  Можливо, ви знайдете відповідь у цих довідкових матеріалах
                </div>
                {doc.suggested.map((a) => (
                  <button key={a.slug} type="button" className="supw__hint"
                    onClick={() => { setOpen(false); navigate(`/help/${a.slug}`); }}>
                    {a.title}
                  </button>
                ))}
              </div>
            )}
            {doc?.messages.map((m) => (
              <div key={m.id}
                className={`supw__msg${m.author_kind === 'user' ? ' own' : ''}`}>
                {m.body}
              </div>
            ))}
          </div>

          <div className="supw__compose">
            <textarea value={draft} rows={2} maxLength={8000}
              placeholder="Напишіть повідомлення"
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && draft.trim()) {
                  e.preventDefault(); send.mutate();
                }
              }} />
            <button type="button" className="supw__send" title="Надіслати"
              aria-label="Надіслати"
              disabled={!draft.trim() || send.isPending}
              onClick={() => send.mutate()}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M4 12l16-8-6 16-2.5-6.5L4 12z" stroke="currentColor"
                  strokeWidth="2" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
          <div className="supw__opts">
            <label>
              <input type="checkbox" checked={withShot}
                onChange={(e) => setWithShot(e.target.checked)} />
              надіслати знімок екрана
            </label>
          </div>
        </aside>
      )}

      <button type="button" className="supw__fab"
        title={unread > 0 ? `Служба підтримки: нових відповідей — ${unread}` : 'Служба підтримки'}
        aria-label={unread > 0 ? `Служба підтримки, нових відповідей: ${unread}` : 'Служба підтримки'}
        onClick={() => setOpen((o) => !o)}>
        {open ? '✕' : '?'}
        {unread > 0 && (
          <span className="supw__badge" data-testid="supw-unread">{unread > 9 ? '9+' : unread}</span>
        )}
      </button>
    </>
  );
}
